<template>
  <div class="pricing-month-grid-wrap">
    <table class="table table-bordered table-condensed pricing-month-grid">
      <thead>
        <tr>
          <th v-for="(wd, i) in weekdayLabels" :key="wd" :class="{ 'pmg-weekend': i >= 5 }">{{ wd }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(week, wi) in weeks" :key="wi">
          <td
            v-for="(cell, di) in week"
            :key="di"
            :class="classFor(cell, di)"
            :tabindex="cell.date ? 0 : -1"
            v-on:pointerdown="cell.date && onPointerDown(cell.date, $event)"
            v-on:pointerenter="cell.date && $emit('extend', cell.date)"
            v-on:keydown.enter.prevent="cell.date && $emit('pick', cell.date, false)"
          >
            <template v-if="cell.date">
              <div class="pmg-day-number">
                <span class="pmg-day-label">{{ weekdayLabels[di] }}</span>
                <strong>{{ cell.day }}</strong>
              </div>
              <div class="pmg-day-content">
                <slot :date="cell.date" :day="cell.day" :past="isPast(cell.date)"></slot>
              </div>
            </template>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
// Month grid of the price calendars. Pressing a day emits pick(date, extend) (extend: Shift held),
// moving the mouse over days while pressed emits extend(date). Past days are dimmed but can be
// edited. The content of a day comes from the default slot ({ date, day, past }) and extra classes
// from cellClass(date, selected): pmg-changed marks a day with dated changes, pmg-closed a closed day.
// In a narrow container the month is a list of days.
export default {
  props: {
    month: { type: Date, required: true },
    rangeFrom: { type: String, default: '' },
    rangeTo: { type: String, default: '' },
    cellClass: { type: Function, default: null }
  },
  emits: ['pick', 'extend'],
  computed: {
    weekdayLabels () {
      return [this._('Mon'), this._('Tue'), this._('Wed'), this._('Thu'), this._('Fri'), this._('Sat'), this._('Sun')]
    },
    today () {
      const d = new Date()
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
    },
    weeks () {
      const year = this.month.getFullYear()
      const month = this.month.getMonth()
      const startOffset = (new Date(year, month, 1).getDay() + 6) % 7
      const daysInMonth = new Date(year, month + 1, 0).getDate()
      const cells = []
      for (let i = 0; i < startOffset; i++) {
        cells.push({})
      }
      for (let d = 1; d <= daysInMonth; d++) {
        const date = year + '-' + String(month + 1).padStart(2, '0') + '-' + String(d).padStart(2, '0')
        cells.push({ date, day: d })
      }
      const weeks = []
      for (let i = 0; i < cells.length; i += 7) {
        weeks.push(cells.slice(i, i + 7))
      }
      while (weeks[weeks.length - 1] && weeks[weeks.length - 1].length < 7) {
        weeks[weeks.length - 1].push({})
      }
      return weeks
    }
  },
  methods: {
    isPast (date) {
      return date < this.today
    },
    onPointerDown (date, event) {
      if (event.button !== undefined && event.button !== 0) {
        return
      }
      if (event.pointerType === 'mouse') {
        event.preventDefault() // No text selection while dragging over days
      }
      this.$emit('pick', date, !!event.shiftKey)
    },
    classFor (cell, index) {
      if (!cell.date) {
        return 'pmg-empty' + (index >= 5 ? ' pmg-weekend' : '')
      }
      const classes = ['pmg-day']
      if (index >= 5) {
        classes.push('pmg-weekend')
      }
      if (this.isPast(cell.date)) {
        classes.push('pmg-past')
      }
      if (cell.date === this.today) {
        classes.push('pmg-today')
      }
      const selected = !!(this.rangeFrom && this.rangeTo && cell.date >= this.rangeFrom && cell.date <= this.rangeTo)
      if (selected) {
        classes.push('pmg-selected')
      }
      const extra = this.cellClass ? this.cellClass(cell.date, selected) : ''
      if (extra) {
        classes.push(extra)
      }
      return classes.join(' ')
    }
  }
}
</script>

<style scoped>
.pricing-month-grid {
  margin-bottom: 0;
  table-layout: fixed;
  background: #fff
}
.pricing-month-grid th {
  font-weight: 600;
  color: #555
}
.pricing-month-grid .pmg-weekend {
  background-color: #fafafa
}
.pricing-month-grid td {
  vertical-align: top;
  padding: 4px
}
.pricing-month-grid td.pmg-empty {
  background-color: #f5f5f5
}
.pricing-month-grid td.pmg-day {
  cursor: pointer;
  min-width: 64px;
  height: 84px;
  user-select: none;
  transition: background-color 0.1s
}
.pricing-month-grid td.pmg-day:hover,
.pricing-month-grid td.pmg-day:focus-visible {
  outline: 2px solid #337ab7;
  outline-offset: -2px
}
.pricing-month-grid td.pmg-past {
  background-color: #fafafa
}
.pricing-month-grid td.pmg-past .pmg-day-number,
.pricing-month-grid td.pmg-past .pmg-day-content {
  opacity: 0.45
}
.pricing-month-grid td.pmg-closed {
  box-shadow: inset 0 3px 0 #d9534f
}
/* The selected range: a ring and a pale tint, so it doesn't look like the blue chips of dated changes */
.pricing-month-grid td.pmg-day.pmg-selected {
  background-color: #f2f7fd;
  box-shadow: inset 0 0 0 2px #337ab7
}
.pricing-month-grid td.pmg-day.pmg-selected.pmg-closed {
  box-shadow: inset 0 3px 0 #d9534f, inset 0 0 0 2px #337ab7
}
.pricing-month-grid td.pmg-past.pmg-selected .pmg-day-number,
.pricing-month-grid td.pmg-past.pmg-selected .pmg-day-content {
  opacity: 0.7
}
.pmg-day-number {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 3px;
  line-height: 22px
}
.pmg-day-number strong {
  display: inline-block;
  min-width: 22px;
  height: 22px;
  border-radius: 11px;
  text-align: center;
  font-weight: 600
}
.pmg-today .pmg-day-number strong {
  background: #337ab7;
  color: #fff
}
.pmg-changed .pmg-day-number::after { /* A dated change on the day */
  content: '';
  width: 7px;
  height: 7px;
  margin-left: auto;
  border-radius: 50%;
  background: #31708f
}
.pmg-day-label {
  display: none
}
/* Narrow: the month as a list of days */
@container pricing-calendar (max-width: 680px) {
  .pricing-month-grid,
  .pricing-month-grid tbody,
  .pricing-month-grid tr,
  .pricing-month-grid td {
    display: block;
    width: 100%
  }
  .pricing-month-grid {
    border: 0
  }
  .pricing-month-grid thead,
  .pricing-month-grid td.pmg-empty {
    display: none
  }
  .pricing-month-grid > tbody > tr > td.pmg-day {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    height: auto;
    min-height: 0;
    padding: 8px 6px;
    border-width: 0 0 1px 0
  }
  .pmg-day-number {
    flex: 0 0 64px;
    margin: 0
  }
  .pmg-day-label {
    display: inline;
    color: #777;
    width: 26px
  }
  .pmg-changed .pmg-day-number::after {
    margin-left: 4px
  }
  .pmg-day-content {
    flex: 1 1 auto;
    min-width: 0
  }
}
</style>
